/**
 * StockLeakageContext — discipline layer over WarehouseInventoryContext.
 *
 * Owns:
 *   • Reservations (lock stock to a job at staging trigger)
 *   • Field purchase log (retail buys with reason code)
 *   • Cycle counts (ABC tiered)
 *   • Variance events (audit trail)
 *   • Stock policies (ABC class, min/max, unit cost)
 *
 * Does NOT own physical on-hand counts — those come from
 * WarehouseInventoryContext. We read its `stockRecords` to compute
 * "expected on-hand" and derive leakage.
 *
 * All write paths are reason-coded and actor-stamped (proof-based).
 */
import { createContext, useCallback, useContext, useMemo, useState, ReactNode, useEffect } from "react";
import {
  AbcClass, CountDueRow, COUNT_CADENCE_DAYS, CycleCount, FieldPurchase,
  StockPolicy, StockReservation, VarianceEvent, VarianceReason,
} from "./types";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";

function newId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

/* ──────────────────────────────────────────────────────────────────── */

interface StockLeakageContextValue {
  policies: StockPolicy[];
  reservations: StockReservation[];
  fieldPurchases: FieldPurchase[];
  cycleCounts: CycleCount[];
  varianceEvents: VarianceEvent[];

  /* ── Reservations ── */
  reserveForJob: (input: {
    jobId: string; jobLabel: string;
    items: Array<{ sku: string; productName: string; quantity: number }>;
    reservedBy?: string;
  }) => { ok: boolean; reservationIds: string[]; shorts: Array<{ sku: string; needed: number; available: number }> };
  releaseReservation: (id: string, reason: string) => void;
  pickReservation: (id: string, pickedBy: string) => void;

  /* ── Field purchases ── */
  logFieldPurchase: (input: Omit<FieldPurchase, "id" | "reviewStatus" | "purchasedAt"> & { purchasedAt?: string }) => string;
  reviewFieldPurchase: (id: string, decision: "approved" | "flagged", reviewer: string, note?: string) => void;

  /* ── Cycle counts ── */
  recordCycleCount: (input: {
    sku: string; productName: string; abcClass: AbcClass;
    systemQuantity: number; countedQuantity: number;
    countedBy: string;
    varianceReason?: VarianceReason; varianceNote?: string;
  }) => { cycleCountId: string; varianceEventId?: string };

  /* ── Derived ── */
  countDueRows: CountDueRow[];
  reorderSuggestions: Array<{ sku: string; productName: string; available: number; min: number; max: number; suggestQty: number }>;
  shrinkageByYear: number;          // $ — cumulative cost impact
  fieldPurchaseRate: { jobsWithPurchase: number; totalActiveJobs: number; pct: number };
  topLeakingSkus: Array<{ sku: string; productName: string; netDelta: number; costImpact: number }>;
  getPolicy: (sku: string) => StockPolicy | undefined;
  getReservationsForJob: (jobId: string) => StockReservation[];
}

const StockLeakageContext = createContext<StockLeakageContextValue | undefined>(undefined);

export function StockLeakageProvider({ children }: { children: ReactNode }) {
  const { stockRecords } = useWarehouseInventory();
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  const [policies, setPolicies] = useState<StockPolicy[]>([]);
  const [reservations, setReservations] = useState<StockReservation[]>([]);
  const [fieldPurchases, setFieldPurchases] = useState<FieldPurchase[]>([]);
  const [cycleCounts, setCycleCounts] = useState<CycleCount[]>([]);
  const [varianceEvents, setVarianceEvents] = useState<VarianceEvent[]>([]);

  useEffect(() => {
    if (deferHeavyProvider) return;

    let alive = true;
    void (async () => {
      try {
        const [policyRes, reservationRes, purchaseRes, countRes, varianceRes] = await Promise.all([
          supabase.from("stock_policies").select("*"),
          supabase.from("stock_reservations").select("*"),
          supabase.from("field_purchases").select("*"),
          supabase.from("cycle_counts").select("*").order("counted_at", { ascending: false }),
          supabase.from("variance_events").select("*").order("recorded_at", { ascending: false }),
        ]);

        if (!alive) return;

        if (!policyRes.error && policyRes.data) {
          setPolicies(policyRes.data.map((r: any) => ({
            sku: String(r.sku ?? ""),
            abcClass: (r.abc_class ?? "C") as AbcClass,
            minQuantity: Number(r.min_quantity ?? 0),
            maxQuantity: Number(r.max_quantity ?? 0),
            unitCost: Number(r.unit_cost ?? 0),
            lastCountedAt: r.last_counted_at ?? undefined,
          })));
        }
        if (!reservationRes.error && reservationRes.data) {
          setReservations(reservationRes.data.map((r: any) => ({
            id: String(r.id),
            jobId: String(r.job_id),
            jobLabel: String(r.job_id),
            sku: String(r.sku ?? ""),
            productName: String(r.product_name ?? r.sku ?? ""),
            quantity: Number(r.quantity ?? 0),
            status: r.status,
            reservedAt: r.reserved_at ?? new Date().toISOString(),
            reservedBy: r.reserved_by ?? "system",
            releaseReason: r.release_reason ?? undefined,
            pickedAt: r.picked_at ?? undefined,
            pickedBy: r.picked_by ?? undefined,
          })));
        }
        if (!purchaseRes.error && purchaseRes.data) {
          setFieldPurchases(purchaseRes.data.map((r: any) => ({
            id: String(r.id),
            jobId: String(r.job_id ?? ""),
            jobLabel: String(r.job_id ?? ""),
            purchasedBy: String(r.purchased_by ?? "system"),
            purchasedAt: r.purchased_at ?? new Date().toISOString(),
            vendor: String(r.vendor ?? ""),
            amount: Number(r.amount ?? 0),
            items: [],
            reason: r.reason,
            notes: r.notes ?? undefined,
            reviewStatus: r.review_status,
            reviewedBy: r.reviewed_by ?? undefined,
            reviewedAt: r.reviewed_at ?? undefined,
          })));
        }
        if (!countRes.error && countRes.data) {
          setCycleCounts(countRes.data.map((r: any) => ({
            id: String(r.id),
            sku: String(r.sku ?? ""),
            productName: String(r.product_name ?? r.sku ?? ""),
            abcClass: (r.abc_class ?? "C") as AbcClass,
            systemQuantity: Number(r.system_quantity ?? 0),
            countedQuantity: Number(r.counted_quantity ?? 0),
            variance: Number(r.variance ?? 0),
            countedBy: String(r.counted_by ?? "system"),
            countedAt: r.counted_at ?? new Date().toISOString(),
            varianceReason: r.variance_reason ?? undefined,
            varianceNote: r.variance_note ?? undefined,
            varianceEventId: r.variance_event_id ?? undefined,
          })));
        }
        if (!varianceRes.error && varianceRes.data) {
          setVarianceEvents(varianceRes.data.map((r: any) => ({
            id: String(r.id),
            sku: String(r.sku ?? ""),
            productName: String(r.product_name ?? r.sku ?? ""),
            quantityDelta: Number(r.quantity_delta ?? 0),
            reason: r.reason,
            reasonNote: r.reason_note ?? undefined,
            cycleCountId: r.cycle_count_id ?? undefined,
            costImpact: r.cost_impact != null ? Number(r.cost_impact) : undefined,
            recordedBy: String(r.recorded_by ?? "system"),
            recordedAt: r.recorded_at ?? new Date().toISOString(),
          })));
        }
      } catch (error) {
        console.warn("[stockLeakage] load failed", error);
      }
    })();
    return () => {
      alive = false;
    };
  }, [deferHeavyProvider]);

  const policyMap = useMemo(() => {
    const m = new Map<string, StockPolicy>();
    policies.forEach(p => m.set(p.sku, p));
    return m;
  }, [policies]);

  const stockMap = useMemo(() => {
    const m = new Map<string, typeof stockRecords[0]>();
    stockRecords.forEach(s => m.set(s.sku, s));
    return m;
  }, [stockRecords]);

  /* ── Reserve ───────────────────────────────────────────────────── */
  const reserveForJob = useCallback<StockLeakageContextValue["reserveForJob"]>((input) => {
    const reservedBy = input.reservedBy || "Auto · Staging trigger";
    const newReservations: StockReservation[] = [];
    const shorts: Array<{ sku: string; needed: number; available: number }> = [];

    // Compute current pending reservations per SKU so we don't double-count.
    const pendingBySku = new Map<string, number>();
    reservations.forEach(r => {
      if (r.status === "reserved") {
        pendingBySku.set(r.sku, (pendingBySku.get(r.sku) || 0) + r.quantity);
      }
    });

    for (const item of input.items) {
      const stock = stockMap.get(item.sku);
      const onHand = stock?.currentStock ?? 0;
      const alreadyReserved = pendingBySku.get(item.sku) || 0;
      const available = Math.max(0, onHand - alreadyReserved);

      if (available < item.quantity) {
        shorts.push({ sku: item.sku, needed: item.quantity, available });
      }

      newReservations.push({
        id: newId("rsv"),
        jobId: input.jobId,
        jobLabel: input.jobLabel,
        sku: item.sku,
        productName: item.productName,
        quantity: item.quantity,
        status: available < item.quantity ? "shorted" : "reserved",
        reservedAt: new Date().toISOString(),
        reservedBy,
      });
    }

    setReservations(prev => [...prev, ...newReservations]);
    void (async () => {
      try {
        const organizationId = await getCurrentOrganizationId().catch(() => null);
        if (!organizationId || newReservations.length === 0) return;
        const rows = newReservations.map((r) => ({
          organization_id: organizationId,
          job_id: r.jobId,
          sku: r.sku,
          product_name: r.productName,
          quantity: r.quantity,
          status: r.status,
          reserved_at: r.reservedAt,
          reserved_by: r.reservedBy,
        }));
        await supabase.from("stock_reservations").insert(rows);
      } catch (error) {
        console.warn("[stockLeakage] reserve persist failed", error);
      }
    })();
    return { ok: shorts.length === 0, reservationIds: newReservations.map(r => r.id), shorts };
  }, [reservations, stockMap]);

  const releaseReservation = useCallback((id: string, reason: string) => {
    setReservations(prev => prev.map(r =>
      r.id === id ? { ...r, status: "released" as const, releaseReason: reason } : r
    ));
    void supabase.from("stock_reservations").update({
      status: "released",
      release_reason: reason,
    }).eq("id", id);
  }, []);

  const pickReservation = useCallback((id: string, pickedBy: string) => {
    const pickedAt = new Date().toISOString();
    setReservations(prev => prev.map(r =>
      r.id === id ? {
        ...r, status: "picked" as const,
        pickedAt, pickedBy,
      } : r
    ));
    void supabase.from("stock_reservations").update({
      status: "picked",
      picked_at: pickedAt,
      picked_by: pickedBy,
    }).eq("id", id);
  }, []);

  /* ── Field Purchase ────────────────────────────────────────────── */
  const logFieldPurchase = useCallback<StockLeakageContextValue["logFieldPurchase"]>((input) => {
    const id = newId("fp");
    const fp: FieldPurchase = {
      ...input,
      id,
      reviewStatus: "pending",
      purchasedAt: input.purchasedAt || new Date().toISOString(),
    };
    setFieldPurchases(prev => [fp, ...prev]);
    void (async () => {
      try {
        const organizationId = await getCurrentOrganizationId().catch(() => null);
        if (!organizationId) return;
        await supabase.from("field_purchases").insert({
          organization_id: organizationId,
          job_id: input.jobId || null,
          vendor: input.vendor,
          amount: input.amount,
          reason: input.reason,
          notes: input.notes ?? null,
          purchased_by: input.purchasedBy,
          purchased_at: fp.purchasedAt,
          review_status: "pending",
        });
      } catch (error) {
        console.warn("[stockLeakage] field purchase persist failed", error);
      }
    })();
    return id;
  }, []);

  const reviewFieldPurchase = useCallback((id: string, decision: "approved" | "flagged", reviewer: string, note?: string) => {
    const reviewedAt = new Date().toISOString();
    setFieldPurchases(prev => prev.map(f =>
      f.id === id ? {
        ...f, reviewStatus: decision, reviewedBy: reviewer,
        reviewedAt, reviewNote: note,
      } : f
    ));
    void supabase.from("field_purchases").update({
      review_status: decision,
      reviewed_by: reviewer,
      reviewed_at: reviewedAt,
    }).eq("id", id);
  }, []);

  /* ── Cycle Count ───────────────────────────────────────────────── */
  const recordCycleCount = useCallback<StockLeakageContextValue["recordCycleCount"]>((input) => {
    const variance = input.countedQuantity - input.systemQuantity;
    const cycleCountId = newId("cc");
    let varianceEventId: string | undefined;

    if (variance !== 0) {
      const policy = policyMap.get(input.sku);
      varianceEventId = newId("ve");
      const event: VarianceEvent = {
        id: varianceEventId,
        sku: input.sku,
        productName: input.productName,
        quantityDelta: variance,
        reason: input.varianceReason || "cycle_count",
        reasonNote: input.varianceNote,
        cycleCountId,
        costImpact: policy ? Math.abs(variance) * policy.unitCost : undefined,
        recordedBy: input.countedBy,
        recordedAt: new Date().toISOString(),
      };
      setVarianceEvents(prev => [event, ...prev]);
    }

    const cc: CycleCount = {
      id: cycleCountId,
      sku: input.sku,
      productName: input.productName,
      abcClass: input.abcClass,
      systemQuantity: input.systemQuantity,
      countedQuantity: input.countedQuantity,
      variance,
      countedBy: input.countedBy,
      countedAt: new Date().toISOString(),
      varianceReason: variance !== 0 ? (input.varianceReason || "cycle_count") : undefined,
      varianceNote: input.varianceNote,
      varianceEventId,
    };
    setCycleCounts(prev => [cc, ...prev]);
    void (async () => {
      try {
        const organizationId = await getCurrentOrganizationId().catch(() => null);
        if (!organizationId) return;
        const ccInsert = await supabase.from("cycle_counts").insert({
          organization_id: organizationId,
          sku: cc.sku,
          product_name: cc.productName,
          abc_class: cc.abcClass,
          system_quantity: cc.systemQuantity,
          counted_quantity: cc.countedQuantity,
          variance: cc.variance,
          counted_by: cc.countedBy,
          counted_at: cc.countedAt,
          variance_reason: cc.varianceReason ?? null,
          variance_note: cc.varianceNote ?? null,
        }).select("id").maybeSingle();
        if (ccInsert.error) return;
        if (variance !== 0 && varianceEventId) {
          const ve = varianceEvents.find((v) => v.id === varianceEventId);
          await supabase.from("variance_events").insert({
            organization_id: organizationId,
            sku: input.sku,
            product_name: input.productName,
            quantity_delta: variance,
            reason: input.varianceReason || "cycle_count",
            reason_note: input.varianceNote ?? null,
            cycle_count_id: ccInsert.data?.id ?? null,
            cost_impact: ve?.costImpact ?? null,
            recorded_by: input.countedBy,
            recorded_at: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.warn("[stockLeakage] cycle count persist failed", error);
      }
    })();
    return { cycleCountId, varianceEventId };
  }, [policyMap, varianceEvents]);

  /* ── Derived: count-due rows ───────────────────────────────────── */
  const countDueRows = useMemo<CountDueRow[]>(() => {
    return policies.map(p => {
      const cadence = COUNT_CADENCE_DAYS[p.abcClass];
      const stock = stockMap.get(p.sku);
      const last = p.lastCountedAt ? new Date(p.lastCountedAt).getTime() : null;
      const daysSince = last !== null ? Math.floor((Date.now() - last) / 86400000) : null;
      const daysUntilDue = daysSince === null ? -999 : cadence - daysSince;
      return {
        sku: p.sku,
        productName: stock?.productName || p.sku,
        abcClass: p.abcClass,
        daysSinceLastCount: daysSince,
        daysUntilDue,
        isOverdue: daysUntilDue < 0,
      };
    }).sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  }, [policies, stockMap]);

  /* ── Derived: reorder suggestions (min/max) ────────────────────── */
  const reorderSuggestions = useMemo(() => {
    const out: Array<{ sku: string; productName: string; available: number; min: number; max: number; suggestQty: number }> = [];
    policies.forEach(p => {
      const stock = stockMap.get(p.sku);
      if (!stock) return;
      const reservedHere = reservations
        .filter(r => r.sku === p.sku && r.status === "reserved")
        .reduce((sum, r) => sum + r.quantity, 0);
      const available = Math.max(0, stock.currentStock - reservedHere);
      if (available <= p.minQuantity) {
        out.push({
          sku: p.sku,
          productName: stock.productName,
          available,
          min: p.minQuantity,
          max: p.maxQuantity,
          suggestQty: p.maxQuantity - available,
        });
      }
    });
    return out;
  }, [policies, stockMap, reservations]);

  /* ── Derived: $ shrinkage YTD ──────────────────────────────────── */
  const shrinkageByYear = useMemo(() => {
    const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
    return varianceEvents
      .filter(e => new Date(e.recordedAt).getTime() >= yearStart && e.quantityDelta < 0)
      .reduce((sum, e) => sum + (e.costImpact || 0), 0);
  }, [varianceEvents]);

  /* ── Derived: field purchase rate (last 90 days) ───────────────── */
  const fieldPurchaseRate = useMemo(() => {
    const cutoff = Date.now() - 90 * 86400000;
    const recent = fieldPurchases.filter(f => new Date(f.purchasedAt).getTime() >= cutoff);
    const jobsWithPurchase = new Set(recent.map(f => f.jobId)).size;
    // Active jobs proxy: any job touched by reservations or purchases in window.
    const allJobIds = new Set<string>();
    reservations.forEach(r => allJobIds.add(r.jobId));
    recent.forEach(f => allJobIds.add(f.jobId));
    const totalActiveJobs = Math.max(allJobIds.size, jobsWithPurchase);
    return {
      jobsWithPurchase,
      totalActiveJobs,
      pct: totalActiveJobs > 0 ? Math.round((jobsWithPurchase / totalActiveJobs) * 100) : 0,
    };
  }, [fieldPurchases, reservations]);

  /* ── Derived: top leaking SKUs (by $ impact) ───────────────────── */
  const topLeakingSkus = useMemo(() => {
    const bySku = new Map<string, { sku: string; productName: string; netDelta: number; costImpact: number }>();
    varianceEvents.forEach(e => {
      const cur = bySku.get(e.sku) || { sku: e.sku, productName: e.productName, netDelta: 0, costImpact: 0 };
      cur.netDelta += e.quantityDelta;
      if (e.quantityDelta < 0) cur.costImpact += e.costImpact || 0;
      bySku.set(e.sku, cur);
    });
    return Array.from(bySku.values())
      .filter(r => r.netDelta < 0)
      .sort((a, b) => b.costImpact - a.costImpact)
      .slice(0, 5);
  }, [varianceEvents]);

  const getPolicy = useCallback((sku: string) => policyMap.get(sku), [policyMap]);
  const getReservationsForJob = useCallback(
    (jobId: string) => reservations.filter(r => r.jobId === jobId),
    [reservations],
  );

  const value: StockLeakageContextValue = {
    policies, reservations, fieldPurchases, cycleCounts, varianceEvents,
    reserveForJob, releaseReservation, pickReservation,
    logFieldPurchase, reviewFieldPurchase,
    recordCycleCount,
    countDueRows, reorderSuggestions, shrinkageByYear, fieldPurchaseRate, topLeakingSkus,
    getPolicy, getReservationsForJob,
  };

  return (
    <StockLeakageContext.Provider value={value}>
      {children}
    </StockLeakageContext.Provider>
  );
}

export function useStockLeakage(): StockLeakageContextValue {
  const ctx = useContext(StockLeakageContext);
  if (!ctx) throw new Error("useStockLeakage must be used within StockLeakageProvider");
  return ctx;
}
